import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { CalculationMode, CalculationResult, EmiHistoryItem } from './emi-calculator';
import {
  performCalculation,
  computeAmortizationSchedule,
  groupScheduleByYear,
  loadEmiHistory,
  saveEmiHistoryItem,
  deleteEmiHistoryItem,
  clearEmiHistory,
  LoanParameters,
  PaymentSummary,
  AmortizationSchedule,
  HistoryModal,
} from './emi-calculator';

export function EmiCalculatorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAssociate = location.pathname.startsWith('/associate');

  const [calcMode, setCalcMode] = useState<CalculationMode>('emi');

  const [amountStr, setAmountStr] = useState<string>('');
  const [rateStr, setRateStr] = useState<string>('');
  const [yearsStr, setYearsStr] = useState<string>('');
  const [monthsStr, setMonthsStr] = useState<string>('');
  const [emiStr, setEmiStr] = useState<string>('');

  const [calcResult, setCalcResult] = useState<CalculationResult>({
    mode: 'emi',
    resolvedPrincipal: 0,
    resolvedRate: 0,
    resolvedTotalMonths: 0,
    resolvedYears: 0,
    resolvedMonths: 0,
    resolvedEmi: 0,
    error: null,
    isCalculated: false,
  });

  const [viewMode, setViewMode] = useState<'yearly' | 'monthly'>('yearly');
  const [expandedYears, setExpandedYears] = useState<Record<number, boolean>>({});

  const [history, setHistory] = useState<EmiHistoryItem[]>(() => loadEmiHistory());
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const amount = useMemo(() => {
    const parsed = parseFloat(amountStr);
    return isNaN(parsed) || parsed < 0 ? 0 : parsed;
  }, [amountStr]);

  const rate = useMemo(() => {
    const parsed = parseFloat(rateStr);
    return isNaN(parsed) || parsed < 0 ? 0 : parsed;
  }, [rateStr]);

  const years = useMemo(() => {
    const parsed = parseInt(yearsStr, 10);
    return isNaN(parsed) || parsed < 0 ? 0 : parsed;
  }, [yearsStr]);

  const months = useMemo(() => {
    const parsed = parseInt(monthsStr, 10);
    return isNaN(parsed) || parsed < 0 ? 0 : parsed;
  }, [monthsStr]);

  const emiInputVal = useMemo(() => {
    const parsed = parseFloat(emiStr);
    return isNaN(parsed) || parsed < 0 ? 0 : parsed;
  }, [emiStr]);

  const totalMonthsInput = useMemo(() => years * 12 + months, [years, months]);

  const handleModeChange = (newMode: CalculationMode) => {
    setCalcMode(newMode);
  };
  const handleCalculateClick = () => {
    const result = performCalculation(calcMode, amount, rate, totalMonthsInput, emiInputVal);
    setCalcResult(result);

    if (!result.error && result.isCalculated) {
      const summary = computeAmortizationSchedule(
        result.resolvedPrincipal,
        result.resolvedRate,
        result.resolvedTotalMonths,
        result.resolvedEmi,
      );

      const updatedHistory = saveEmiHistoryItem({
        mode: calcMode,
        principal: result.resolvedPrincipal,
        rate: result.resolvedRate,
        totalMonths: result.resolvedTotalMonths,
        years: result.resolvedYears,
        months: result.resolvedMonths,
        emi: result.resolvedEmi,
        totalInterest: summary.totalInterest,
        totalAmount: summary.totalAmount,
      });
      setHistory(updatedHistory);
    }
  };

  // Clear / Reset Action: Clears all values so user can enter fresh parameters
  const handleClear = () => {
    setAmountStr('');
    setRateStr('');
    setYearsStr('');
    setMonthsStr('');
    setEmiStr('');
    setCalcResult({
      mode: calcMode,
      resolvedPrincipal: 0,
      resolvedRate: 0,
      resolvedTotalMonths: 0,
      resolvedYears: 0,
      resolvedMonths: 0,
      resolvedEmi: 0,
      error: null,
      isCalculated: false,
    });
  };

  // History Actions: Load from history, delete record, clear all records
  const handleSelectHistoryItem = (item: EmiHistoryItem) => {
    setCalcMode(item.mode);
    setAmountStr(String(item.principal));
    setRateStr(item.rate % 1 === 0 ? String(item.rate) : item.rate.toFixed(2));
    setYearsStr(String(item.years));
    setMonthsStr(String(item.months));
    setEmiStr(item.emi.toFixed(2));
    setCalcResult({
      mode: item.mode,
      resolvedPrincipal: item.principal,
      resolvedRate: item.rate,
      resolvedTotalMonths: item.totalMonths,
      resolvedYears: item.years,
      resolvedMonths: item.months,
      resolvedEmi: item.emi,
      error: null,
      isCalculated: true,
    });
    setIsHistoryOpen(false);
  };

  const handleDeleteHistoryItem = (id: string) => {
    const updated = deleteEmiHistoryItem(id);
    setHistory(updated);
  };

  const handleClearAllHistory = () => {
    clearEmiHistory();
    setHistory([]);
  };

  // Summary & Amortization Schedule based on resolved calculation results
  const calculation = useMemo(() => {
    const P = calcResult.resolvedPrincipal;
    const annualRate = calcResult.resolvedRate;
    const N = calcResult.resolvedTotalMonths;
    const emi = calcResult.resolvedEmi;

    if (calcResult.error || !calcResult.isCalculated) {
      return computeAmortizationSchedule(0, 0, 0, 0);
    }

    return computeAmortizationSchedule(P, annualRate, N, emi);
  }, [calcResult]);

  // Group Schedule by Year
  const yearlySchedule = useMemo(() => {
    return groupScheduleByYear(calculation.schedule);
  }, [calculation.schedule]);

  const toggleYearExpand = (yNum: number) => {
    setExpandedYears((prev) => ({ ...prev, [yNum]: !prev[yNum] }));
  };

  const isCurrentModeCalculated = calcResult.mode === calcMode && calcResult.isCalculated;

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8 pb-24">
      {/* Back to Menu / Dashboard Button (Mobile Only) */}
      <button
        type="button"
        onClick={() => navigate(isAssociate ? '/associate/transactions' : '/client/menu')}
        className="md:hidden inline-flex items-center gap-1.5 mb-4 text-xs font-bold text-gray-700 hover:text-brand-800 bg-white border border-gray-200 hover:border-brand-300 px-3 py-1.5 rounded-xl shadow-xs transition-all active:scale-95 cursor-pointer select-none"
      >
        <svg
          className="w-4 h-4 text-brand-700"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        <span>{isAssociate ? 'Back to Dashboard' : 'Back to Menu'}</span>
      </button>

      {/* Header Card */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white rounded-3xl p-6 text-gray-900 border border-gray-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-extrabold sm:text-2xl tracking-tight text-gray-900">
              Loan EMI Calculator
            </h1>
            {/* <span className="hidden sm:inline-flex items-center text-xs font-bold text-brand-700 bg-brand-50 border border-brand-200 px-2.5 py-0.5 rounded-full">
              4-Way Solver
            </span> */}
          </div>
          <p className="mt-1 text-sm text-gray-600 max-w-xl leading-relaxed">
            Select the radio button beside the value you do not know, fill in the other three
            values, and click Calculate.
          </p>
        </div>

        {/* Header Action Buttons: History + Quick Test Case Trigger */}
        <div className="flex items-center gap-2 self-start md:self-auto flex-wrap">
          <button
            type="button"
            onClick={() => setIsHistoryOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-brand-50 border border-gray-200 hover:border-brand-300 rounded-xl text-xs font-bold text-gray-700 hover:text-brand-800 transition-all shadow-2xs cursor-pointer"
            title="View calculation history"
          >
            <svg
              className="w-3.5 h-3.5 text-brand-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>History</span>
            {history.length > 0 && (
              <span className="ml-0.5 px-1.5 py-0.2 bg-brand-100 text-brand-800 rounded-full text-[10px] font-extrabold">
                {history.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Main Grid: Inputs vs Results */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Calculator Controls */}
        <LoanParameters
          calcMode={calcMode}
          onModeChange={handleModeChange}
          amountStr={amountStr}
          setAmountStr={setAmountStr}
          rateStr={rateStr}
          setRateStr={setRateStr}
          yearsStr={yearsStr}
          setYearsStr={setYearsStr}
          monthsStr={monthsStr}
          setMonthsStr={setMonthsStr}
          emiStr={emiStr}
          setEmiStr={setEmiStr}
          amount={amount}
          rate={rate}
          years={years}
          months={months}
          emiInputVal={emiInputVal}
          totalMonthsInput={totalMonthsInput}
          calcResult={calcResult}
          isCurrentModeCalculated={isCurrentModeCalculated}
          onCalculate={handleCalculateClick}
          onClear={handleClear}
        />

        {/* Right Column: EMI Summary & Visual Donut Chart */}
        <PaymentSummary calculation={calculation} calcResult={calcResult} calcMode={calcMode} />
      </div>

      {/* Amortization Repayment Schedule */}
      <AmortizationSchedule
        calculation={calculation}
        yearlySchedule={yearlySchedule}
        calcResult={calcResult}
        viewMode={viewMode}
        setViewMode={setViewMode}
        expandedYears={expandedYears}
        toggleYearExpand={toggleYearExpand}
      />

      {/* History Modal */}
      <HistoryModal
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        history={history}
        onSelect={handleSelectHistoryItem}
        onDelete={handleDeleteHistoryItem}
        onClearAll={handleClearAllHistory}
      />
    </div>
  );
}
